import { useMemo } from 'react';
import { useOne, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, BookOpen, BarChart3, Pencil, DollarSign, Boxes, CalendarDays, Users, MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormHeader } from '@/components/forms';
import { TabsWithPersistence } from '@/components/shared';
import type { Course } from './types';
import { getCourseTitle } from './types';
import { CourseAnalyticsTab } from './components/CourseAnalyticsTab';
import { CourseEditTab } from './components/CourseEditTab';
import { CoursePricingTab } from './components/CoursePricingTab';
import { CourseModulesTab } from './components/CourseModulesTab';
import { CourseScheduleTab } from './components/CourseScheduleTab';
import { CourseMeetingTab } from './components/CourseMeetingTab';
import { CourseCommentsTab } from './components/CourseCommentsTab';

const SCHEDULE_COURSE_TYPES = new Set(['ONLINE', 'MEETING', 'WEBINAR']);

export function CoursesShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  const { result: course, query } = useOne<Course>({
    resource: 'courses',
    id: String(id),
  });

  const refetch = query.refetch;

  const tabs = useMemo(() => {
    if (!course) {return [];}

    const items = [
      {
        value: 'analytics',
        label: 'Аналитика',
        icon: <BarChart3 className="h-4 w-4" />,
        content: <CourseAnalyticsTab courseId={course.id} />,
      },
      {
        value: 'edit',
        label: 'Редактирование',
        icon: <Pencil className="h-4 w-4" />,
        content: (
          <CourseEditTab
            course={course}
            onUpdated={() => { refetch(); }}
          />
        ),
      },
      ...(course.courseType !== 'MEETING' ? [{
        value: 'pricing',
        label: 'Тарифы',
        icon: <DollarSign className="h-4 w-4" />,
        content: <CoursePricingTab courseId={course.id} />,
      }] : []),
      {
        value: 'modules',
        label: 'Модули',
        icon: <Boxes className="h-4 w-4" />,
        content: (
          <CourseModulesTab
            course={course}
            onRefresh={() => { refetch(); }}
          />
        ),
      },
      {
        value: 'comments',
        label: 'Комментарии',
        icon: <MessageSquare className="h-4 w-4" />,
        content: <CourseCommentsTab courseId={course.id} />,
      },
    ];

    if (SCHEDULE_COURSE_TYPES.has(course.courseType)) {
      items.push({
        value: 'schedule',
        label: 'Расписание',
        icon: <CalendarDays className="h-4 w-4" />,
        content: (
          <CourseScheduleTab
            course={course}
            onUpdated={() => { refetch(); }}
          />
        ),
      });
    }

    if (course.courseType === 'MEETING') {
      items.push({
        value: 'meeting',
        label: 'Клуб',
        icon: <Users className="h-4 w-4" />,
        content: (
          <CourseMeetingTab
            course={course}
            onUpdated={() => { refetch(); }}
          />
        ),
      });
    }

    return items;
  }, [course, refetch]);

  if (query.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Курс не найден</p>
        <Button variant="link" onClick={() => go({ to: '/courses' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const title = getCourseTitle(course);

  return (
    <div className="space-y-6">
      <FormHeader
        title={title}
        onBack={() => go({ to: '/courses' })}
        icon={<BookOpen className="h-5 w-5" />}
        titleAddon={
          course.isPublished ? (
            <Badge className="bg-green-500/10 text-green-500">Опубликован</Badge>
          ) : (
            <Badge variant="secondary">Черновик</Badge>
          )
        }
        sticky
      />

      <TabsWithPersistence
        tabs={tabs}
        defaultTab="analytics"
        persistKey="courses"
        tabsListClassName={
          course.courseType === 'MEETING'
            ? 'grid w-full grid-cols-3 sm:grid-cols-4 lg:inline-flex lg:w-auto'
            : (SCHEDULE_COURSE_TYPES.has(course.courseType)
              ? 'grid w-full grid-cols-3 sm:grid-cols-6 lg:inline-flex lg:w-auto'
              : 'grid w-full grid-cols-3 sm:grid-cols-5 lg:inline-flex lg:w-auto')
        }
      />
    </div>
  );
}
